import { PersonStamdata } from "../interfaces/stamdata";

/**
 * `Guid.Empty`, som .NET serialiserer den. Værdien `PersonStamdata.id` har, når serveren svarede
 * uden en person: feltet er en ikke-nullable `Guid` i backenden, så det bliver aldrig `null`.
 */
export const TOMT_GUID = "00000000-0000-0000-0000-000000000000";

const erRigtigtId = (id: string | null | undefined): boolean => {
  const trimmet = id?.trim().toLowerCase();
  return Boolean(trimmet) && trimmet !== TOMT_GUID;
};

/**
 * Om et svar fra `HentPerson` beskriver en profil, medlemmet faktisk har.
 *
 * `HentPerson` svarer 200 med et default-konstrueret `PersonModel`, når medlemmet ikke har en
 * Person-række i portalen — typisk fordi det endnu ikke er synkroniseret fra AIKA (ADV-272). Det
 * svar er syntaktisk gyldigt og ligner en indlæst profil, og portalens guards er truthy-tests, som
 * et tomt objekt består. Resultatet var, at hele portalen viste tomme felter frem som en profil
 * (ADV-288).
 *
 * To signaler læses, og begge skal holde:
 *
 * - `profilKlar` er backendens eksplicitte svar (ADV-288). Feltet er tilføjet additivt, så det
 *   mangler i den backend, der kører nu.
 * - Et rigtigt `id`. Dette er faldbakken, der gør, at portalen kan deployes før backenden — og
 *   samtidig den, der holder, hvis flaget skulle sige klar med et tomt id: portalen sender feltet
 *   videre som `personId`, og `Guid.Empty` bliver afvist af serveren (ADV-274).
 *
 * Faldbakken er overgangskode: når backendens `profilKlar` er i luften i begge miljøer, er flaget
 * alene contracten, og id-tjekket kan falde bort sammen med det næste skridt i ADV-288 (404 fra
 * `HentPerson` eller et nullable `Id`). Indtil da er den det eneste, der virker mod den kørende
 * backend, så den må ikke fjernes først.
 *
 * Returnerer `false` for et manglende svar, så kaldere kan sende det, de har, uden først at tjekke.
 */
export const erProfilKlar = (
  person: Pick<PersonStamdata, "id" | "profilKlar"> | null | undefined,
): boolean => {
  if (!person) return false;
  if (person.profilKlar === false) return false;
  return erRigtigtId(person.id);
};

/**
 * Hvad portalen ved om medlemmets profil lige nu.
 *
 * `ukendt` er grunden til, at dette ikke er en boolean: en side må kun skrive "din profil er ikke
 * klar", når den faktisk har fået svar. Ellers ville beskeden blinke forbi for hvert medlem med en
 * helt almindelig profil.
 */
export type ProfilStatus = "klar" | "ikkeKlar" | "ukendt";

export interface ProfilStatusInput {
  /**
   * Den friske stamdata-forespørgsel. `undefined` mens den kører, `null` når den har svaret uden en
   * profil (se `getStamdataPerson`) — de to må ikke slås sammen.
   *
   * Skal komme fra forespørgslen — <b>aldrig</b> fra rodrutens loader-data. Loaderens `stamdata` er
   * `null` i to tilfælde: profilen findes ikke, og forespørgslen fejlede. Sendes den herind, bliver
   * en netværksfejl meldt som "din profil er ikke klar", og medlemmet mister beskeden om at prøve at
   * genindlæse siden. Loaderen skelner med sit eget `profilIkkeKlar`-flag, som hører i feltet
   * nedenfor.
   */
  stamdata: PersonStamdata | null | undefined;
  /**
   * Hvad rodrutens loader vidste ved sidste hårde sideindlæsning (`HomeLoaderResponse`). Bruges kun,
   * indtil forespørgslen er hjemme: loader-data genkøres ikke ved klientnavigation, så den kilde er
   * per definition et øjebliksbillede — netop fælden i ADV-204.
   */
  profilIkkeKlarFraLoader?: boolean;
}

/**
 * Slår de to kilder sammen, så en side kan afgøre, om den skal vise profil-beskeden.
 *
 * Den friske forespørgsel har altid ret, når den har svaret. Loaderens svar dækker kun hullet, indtil
 * den er hjemme — og bliver overtrumfet, hvis profilen er blevet klar i mellemtiden.
 */
export const getProfilStatus = ({
  stamdata,
  profilIkkeKlarFraLoader,
}: ProfilStatusInput): ProfilStatus => {
  if (stamdata) return "klar";
  if (stamdata === null) return "ikkeKlar";
  return profilIkkeKlarFraLoader ? "ikkeKlar" : "ukendt";
};
